import { WorklistOrderLabelInfo, type OrderForWorklist, type WorklistOrderProcedureInfo, WorklistOrderUrgencyInfo } from '@/Models/GenericWorklist/WorklistResult';
import { type CellContext, type ColumnDef } from '@tanstack/react-table';
import Style from '../WorklistDataGrid.module.scss';
import { isWorklistColumnSortable, WorklistColumn } from '@/Pages/GenericWorklist/Logic/WorklistProperties';

const {
    'order-procedure-cell': orderProcedureCellStyle,
    'order-procedure-cell_content': orderProcedureCellContentStyle,
} = Style;

interface IWorklistColumnHelper {
    createDataColumnDefinition: <T>(
        data: {
            selectData?: (rowData: OrderForWorklist) => T;
            renderData: (data: T, rowData: OrderForWorklist) => React.ReactNode;
            headerText?: React.ReactElement | string;
            headerContent: React.ReactElement | undefined;
        } & ColumnBaseDefinition,
    ) => ColumnDef<OrderForWorklist, T>;
    createWorklistTextColumn: (data: {
        columnId: WorklistColumn;
        headerText?: string;
        selectText: (rowData: OrderForWorklist) => string;
    } & ColumnBaseDefinition,
    ) => ColumnDef<OrderForWorklist, string>;
    createWorklistCustomColumn: <T, >(data: {
        columnId: WorklistColumn;
        headerText?: string;
        selectData: (rowData: OrderForWorklist) => T;
        renderData: (data: T) => string | React.ReactElement;
    } & ColumnBaseDefinition,
    ) => ColumnDef<OrderForWorklist, any>;
    createWorklistOrderProcedureColumn: <T, >(
        data: {
            selectData?: (orderProcedureInfo: WorklistOrderProcedureInfo) => T;
            renderData: ((data: T) => string | React.ReactElement);
        } & ColumnBaseDefinition,
    ) => ColumnDef<OrderForWorklist, any>;
    createWorklistOrderLabelColumn: <T, >(
        data: {
            selectData?: (orderProcedureInfo: WorklistOrderLabelInfo) => T;
            renderData: ((data: T) => string | React.ReactElement);
        } & ColumnBaseDefinition,
    ) => ColumnDef<OrderForWorklist, any>;
    createWorklistOrderUrgencyColumn: <T, >(
        data: {
            selectData?: (orderUrgencyInfo: WorklistOrderUrgencyInfo) => T;
            renderData: ((data: T) => string | React.ReactElement);
        } & ColumnBaseDefinition,
    ) => ColumnDef<OrderForWorklist, any>;
    createWorklistOrderProcedureTextColumn: (
        data: {
            selectText: (orderProcedureInfo: WorklistOrderProcedureInfo) => string;
        } & ColumnBaseDefinition,
    ) => ColumnDef<OrderForWorklist, any>;
    createStringArrayColumn: (
        data: {
            columnId: WorklistColumn;
            headerText?: string;
            selectData: (rowData: OrderForWorklist) => string[];
            renderData?: (data: string[]) => React.ReactElement;
        },
    ) => ColumnDef<OrderForWorklist, any>;
}

interface ColumnBaseDefinition {
    columnId: WorklistColumn;
    headerText?: string;
}
export const createDataColumnDefinition = <T,>(
    data: {
        selectData?: (rowData: OrderForWorklist) => T;
        renderData: (data: T, rowData: OrderForWorklist) => React.ReactNode;
        headerContent?: React.ReactElement;
    } & ColumnBaseDefinition,
): ColumnDef<OrderForWorklist, T> => {
    const { columnId, headerText, headerContent, selectData, renderData } = data;
    const getHeader = () => {
        if (headerContent) {
            return headerContent;
        }
        if (headerText) {
            return headerText;
        }
        return columnId;
    };

    return {
        id: columnId,
        header: getHeader,
        accessorFn: selectData ?? ((rowData) => rowData as unknown as T),
        cell: (info: CellContext<OrderForWorklist, T>) =>
            renderData(info.getValue(), info.row.original),
        enableSorting: isWorklistColumnSortable(columnId),
    };
};

export const createWorklistCustomColumn = <T,>(
    data: {
        columnId: WorklistColumn;
        headerText?: string;
        selectData: (rowData: OrderForWorklist) => T;
        renderData: (data: any) => string | React.ReactElement;
    } & ColumnBaseDefinition,
): ColumnDef<OrderForWorklist, any> => {
    return createDataColumnDefinition({
        columnId: data.columnId,
        headerText: data.headerText,
        selectData: data.selectData,
        renderData: data.renderData,
    });
};

export const createWorklistOrderUrgencyColumn = <T,>(
    data: {
        selectData?: (orderUrgencyInfo: WorklistOrderUrgencyInfo) => T;
        renderData: ((data: T) => string | React.ReactElement);
    } & ColumnBaseDefinition,
): ColumnDef<OrderForWorklist, any> => {
    const { columnId, headerText, selectData } = data;
    const defaultSelectData = (orderUrgency: WorklistOrderUrgencyInfo): WorklistOrderUrgencyInfo => orderUrgency;
    const selectDataDefined = selectData ?? defaultSelectData;

    return createDataColumnDefinition({
        columnId,
        headerText: headerText ?? columnId,
        selectData: (order) => order.OrderInfo.Urgency,
        renderData: (urgency) => {
            if (!urgency) {
                return <div className={orderProcedureCellStyle} />;
            }

            const selectedData = selectDataDefined(urgency) as T;
            const renderedData = data.renderData(selectedData);

            return (
                <div className={orderProcedureCellStyle}>
                    <div className={orderProcedureCellContentStyle}>{renderedData}</div>
                </div>
            );
        },
    });
};

export const createWorklistTextColumn = (
    data: {
        selectText: (rowData: OrderForWorklist) => string;
    } & ColumnBaseDefinition,
): ColumnDef<OrderForWorklist, string> => {
    return createDataColumnDefinition({
        columnId: data.columnId,
        headerText: data.headerText,
        selectData: data.selectText,
        renderData: (text: string) => {
            return <span>{text}</span>;
        },
    });
};

export const createWorklistOrderProcedureColumn = <T,>(
    data: {
        selectData?: (orderProcedureInfo: WorklistOrderProcedureInfo) => T;
        renderData: ((data: T) => string | React.ReactElement);
    } & ColumnBaseDefinition,
): ColumnDef<OrderForWorklist, any> => {
    const { columnId, headerText, selectData } = data;
    const defaultSelectData = (orderProcedure: WorklistOrderProcedureInfo): WorklistOrderProcedureInfo => orderProcedure;
    const selectDataDefined = selectData ?? defaultSelectData;

    return createDataColumnDefinition({
        columnId,
        headerText: headerText ?? columnId,
        selectData: (order) => order.OrderProceduresInfos,
        renderData: (orderProcedures) => {
            const orderProcedureDatas = orderProcedures.map((orderProcedure) => ({ id: orderProcedure.OrderProcedureID, selectedData: selectDataDefined(orderProcedure) as T }));
            const orderProcedureElements = orderProcedureDatas.map((orderProcedureData) => ({ id: orderProcedureData.id, renderedData: data.renderData(orderProcedureData.selectedData) }));
            const wrappedOrderProcedureElements = orderProcedureElements.map((x) => <div key={`${headerText}-${x.id}`} className={orderProcedureCellContentStyle}>{x.renderedData}</div>);

            return (<div className={orderProcedureCellStyle}>{wrappedOrderProcedureElements}</div>);
        },
    });
};

export const createWorklistOrderLabelColumn = <T,>(
    data: {
        selectData?: (orderLabelInfo: WorklistOrderLabelInfo) => T;
        renderData: ((data: T) => string | React.ReactElement);
    } & ColumnBaseDefinition,
): ColumnDef<OrderForWorklist, any> => {
    const { columnId, headerText, selectData } = data;
    const defaultSelectData = (orderLabel: WorklistOrderLabelInfo): WorklistOrderLabelInfo => orderLabel;
    const selectDataDefined = selectData ?? defaultSelectData;

    return createDataColumnDefinition({
        columnId,
        headerText: headerText ?? columnId,
        selectData: (order) => order.OrderLabelsInfos,
        renderData: (labels) => {
            if (!labels || labels.length === 0) {
                return <div className={orderProcedureCellStyle} />;
            }

            const orderLabelDatas = labels.map((orderLabel) => ({
                id: orderLabel.ID,
                selectedData: selectDataDefined(orderLabel) as T,
            }));
            const orderLabelElements = orderLabelDatas.map((orderLabelData) => ({
                id: orderLabelData.id,
                renderedData: data.renderData(orderLabelData.selectedData),
            }));
            const wrappedOrderLabelElements = orderLabelElements.map((x) =>
                <div key={x.id} className={orderProcedureCellContentStyle}>{x.renderedData}</div>,
            );

            return (<div className={orderProcedureCellStyle}>{wrappedOrderLabelElements}</div>);
        },
    });
};

export const createWorklistOrderProcedureTextColumn = (
    data: {
        selectText: (orderProcedureInfo: WorklistOrderProcedureInfo) => string;
    } & ColumnBaseDefinition,
): ColumnDef<OrderForWorklist, any> => {
    const { columnId, headerText, selectText } = data;

    return createWorklistOrderProcedureColumn({
        columnId,
        headerText: headerText ?? columnId,
        selectData: (orderProcedure) => selectText(orderProcedure),
        renderData: (selectedData) => <span>{selectedData}</span>,
    });
};

export const createStringArrayColumn = (
    data: {
        columnId: WorklistColumn;
        headerText?: string;
        selectData: (rowData: OrderForWorklist) => string[];
        renderData?: (data: string[]) => React.ReactElement;
    },
): ColumnDef<OrderForWorklist, any> => {
    const { columnId, headerText, selectData, renderData } = data;

    return createDataColumnDefinition({
        columnId,
        headerText: headerText ?? columnId,
        selectData,
        renderData: (strings) => {
            const items = [...new Set(strings.filter(Boolean))];
            if (renderData) {
                return renderData(items);
            }
            return (
                <div>
                    {items.map((str, idx) => (
                        <div key={idx + str}>{str}</div>
                    ))}
                </div>
            );
        },
    });
};
export const createWorklistColumnHelper = (): IWorklistColumnHelper => ({
    createDataColumnDefinition,
    createWorklistTextColumn,
    createWorklistCustomColumn,
    createWorklistOrderProcedureColumn,
    createWorklistOrderLabelColumn,
    createWorklistOrderUrgencyColumn,
    createStringArrayColumn,
    createWorklistOrderProcedureTextColumn,
});

export default createWorklistColumnHelper;
